import React, { useState, useEffect, useCallback } from "react";
import { X, Minus, Plus, CheckCircle2, XCircle, Loader2, CreditCard, Lock, ShoppingCart, ArrowLeft, Info } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getQuote, createOrder, getOrder, payOrder, formatCents } from "../../lib/investApi";

const STEP = { QUOTE: "quote", CONFIRM: "confirm", PAYMENT: "payment", PROCESSING: "processing", DONE: "done" };

const STEP_ORDER = [STEP.QUOTE, STEP.CONFIRM, STEP.PAYMENT];
const STEP_LABELS = { [STEP.QUOTE]: "Configure", [STEP.CONFIRM]: "Review", [STEP.PAYMENT]: "Pay" };

const CATEGORY_ACCENT = {
  SUV: "#2563EB", SEDAN: "#E11D48", HATCHBACK: "#059669",
  TRUCK: "#ea580c", SPORTS: "#9333ea",
};
const CATEGORY_ICON = {
  SUV: "🚙", SEDAN: "🚗", HATCHBACK: "🚕", TRUCK: "🛻", SPORTS: "🏎️",
};

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: "15px",
      color: "#111",
      fontFamily: "system-ui, -apple-system, sans-serif",
      fontSmoothing: "antialiased",
      "::placeholder": { color: "#9CA3AF" },
    },
    invalid: { color: "#E11D48", iconColor: "#E11D48" },
  },
};

function StepIndicator({ step }) {
  const activeIdx = STEP_ORDER.indexOf(step);
  if (activeIdx === -1) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      {STEP_ORDER.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <React.Fragment key={s}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%",
                background: done ? "var(--zc-success)" : active ? "var(--zc-secondary)" : "var(--zc-bg-3)",
                border: `2px solid ${done ? "var(--zc-success)" : active ? "var(--zc-secondary)" : "var(--zc-border)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all 0.2s",
              }}>
                {done
                  ? <CheckCircle2 size={12} style={{ color: "#fff" }} />
                  : <span style={{ fontSize: 10, fontWeight: 700, color: active ? "#fff" : "var(--zc-text-3)" }}>{i + 1}</span>
                }
              </div>
              <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? "var(--zc-text)" : "var(--zc-text-3)" }}>
                {STEP_LABELS[s]}
              </span>
            </div>
            {i < STEP_ORDER.length - 1 && (
              <div style={{ width: 24, height: 1.5, background: done ? "var(--zc-success)" : "var(--zc-border)", margin: "0 6px", flexShrink: 0, transition: "background 0.2s" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FeeRow({ label, value, bold, accent }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 14px",
      borderBottom: bold ? "none" : "1px solid var(--zc-border)",
    }}>
      <span style={{ fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: bold ? "var(--zc-text)" : "var(--zc-text-2)" }}>{label}</span>
      <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 500, fontVariantNumeric: "tabular-nums", color: accent ? "var(--zc-secondary)" : bold ? "var(--zc-text)" : "var(--zc-text-2)" }}>{value}</span>
    </div>
  );
}

function StripeCardForm({ clientSecret, onSuccess, totalCents, displayName, qty, accent }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const [focused, setFocused] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const card = elements.getElement(CardElement);
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card },
    });
    if (error) {
      setErr(error.message);
      setBusy(false);
    } else if (paymentIntent.status === "succeeded") {
      onSuccess(paymentIntent.id);
    } else {
      setErr(`Unexpected status: ${paymentIntent.status}`);
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Order summary pill */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "12px 16px", borderRadius: 10,
        background: "var(--zc-bg-3)", border: "1px solid var(--zc-border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ShoppingCart size={14} style={{ color: "var(--zc-text-3)" }} />
          <span style={{ fontSize: 13, color: "var(--zc-text-2)" }}>{qty} × {displayName}</span>
        </div>
        <span style={{ fontWeight: 700, fontSize: 15, color: accent ?? "var(--zc-secondary)" }}>{formatCents(totalCents)}</span>
      </div>

      {/* Card input */}
      <div>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, letterSpacing: "0.03em", color: "var(--zc-text-2)", marginBottom: 7 }}>
          Card Details
        </label>
        <div style={{
          border: `1.5px solid ${focused ? "var(--zc-secondary)" : "var(--zc-border-2)"}`,
          borderRadius: 10, padding: "13px 14px",
          background: "var(--zc-bg-2)",
          transition: "border-color 0.15s",
        }}>
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
      </div>

      {/* Test card hint */}
      <div style={{
        display: "flex", gap: 8, alignItems: "flex-start",
        padding: "9px 12px", borderRadius: 8,
        background: "#F0F9FF", border: "1px solid #BAE6FD",
        fontSize: 11, color: "#0369A1",
      }}>
        <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />
        <span><strong>Test card:</strong> 4242 4242 4242 4242 · Any future date · Any CVC · Any ZIP</span>
      </div>

      {err && (
        <div style={{
          display: "flex", gap: 8, alignItems: "center",
          padding: "10px 12px", borderRadius: 8,
          background: "#FEF2F2", border: "1px solid #FECACA",
          color: "var(--zc-danger)", fontSize: 13,
        }}>
          <XCircle size={14} style={{ flexShrink: 0 }} />
          {err}
        </div>
      )}

      <button
        onClick={handlePay}
        disabled={!stripe || busy}
        className="zc-btn-primary"
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 0", fontSize: 14, fontWeight: 700 }}
      >
        {busy
          ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Processing…</>
          : <><Lock size={14} />Pay {formatCents(totalCents)}</>
        }
      </button>
    </div>
  );
}

export default function InvestBuyModal({ collection, displayName, walletUserId, onClose, onSuccess }) {
  const [step, setStep]             = useState(STEP.QUOTE);
  const [qty, setQty]               = useState(1);
  const [quote, setQuote]           = useState(null);
  const [quoteLoading, setQLoading] = useState(false);
  const [quoteError, setQError]     = useState(null);
  const [order, setOrder]           = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError]           = useState(null);
  const [busy, setBusy]             = useState(false);

  const label  = displayName ?? collection.name;
  const cat    = collection.category;
  const accent = CATEGORY_ACCENT[cat] ?? "var(--zc-secondary)";
  const icon   = CATEGORY_ICON[cat] ?? "🚘";

  const fetchQuote = useCallback(async (q) => {
    setQLoading(true);
    setQError(null);
    try {
      const data = await getQuote(collection.id, q);
      setQuote(data);
    } catch (e) {
      setQError(e.message);
    } finally {
      setQLoading(false);
    }
  }, [collection.id]);

  useEffect(() => { fetchQuote(qty); }, [qty, fetchQuote]);

  useEffect(() => {
    if (step !== STEP.PROCESSING || !order) return;
    const interval = setInterval(async () => {
      try {
        const updated = await getOrder(order.id);
        if (!["PENDING", "PROCESSING", "PAID", "TRANSFERRING", "AWAITING_PAYMENT"].includes(updated.status)) {
          setOrder(updated);
          setStep(STEP.DONE);
          if (updated.status === "COMPLETED") onSuccess?.();
          clearInterval(interval);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [step, order, onSuccess]);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleCreateOrder = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await createOrder({ userId: walletUserId, collectionId: collection.id, quantity: qty });
      setOrder(data.order);
      setClientSecret(data.stripeClientSecret);
      const pk = data.stripePublishableKey;
      if (pk && pk !== "pk_test_REPLACE_ME") setStripePromise(loadStripe(pk));
      setStep(STEP.PAYMENT);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleStripeSuccess = async (paymentIntentId) => {
    setStep(STEP.PROCESSING);
    try {
      await payOrder(order.id, paymentIntentId);
    } catch (e) {
      setOrder(prev => ({ ...prev, status: "TRANSFER_FAILED", errorMessage: e.message }));
      setStep(STEP.DONE);
    }
  };

  const fees = quote?.fees;
  const succeeded = order?.status === "COMPLETED";
  const showStep = [STEP.QUOTE, STEP.CONFIRM, STEP.PAYMENT].includes(step);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog" aria-modal="true"
    >
      <div style={{
        background: "var(--zc-bg-2)",
        borderRadius: 16,
        width: "100%", maxWidth: 460,
        boxShadow: "0 24px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06)",
        overflow: "hidden",
        animation: "modalIn 0.18s ease-out",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--zc-border)",
          background: "var(--zc-bg-2)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Category icon / image badge */}
            {collection.imageUrl ? (
              <img
                src={collection.imageUrl}
                alt={label}
                style={{ width: 34, height: 34, borderRadius: 9, objectFit: "cover", flexShrink: 0 }}
              />
            ) : (
              <div style={{
                width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                background: `${accent}18`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18,
              }}>
                {icon}
              </div>
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--zc-text)", lineHeight: 1.2 }}>
                {step === STEP.DONE
                  ? (succeeded ? "Purchase Complete" : "Purchase Failed")
                  : step === STEP.PROCESSING ? "Transferring Tokens"
                  : label}
              </div>
              {showStep && (
                <div style={{ fontSize: 11, color: "var(--zc-text-3)", marginTop: 1 }}>
                  {collection.symbol ?? cat}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {showStep && <StepIndicator step={step} />}
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1.5px solid var(--zc-border)", background: "transparent",
                cursor: "pointer", color: "var(--zc-text-3)",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--zc-text-2)"; e.currentTarget.style.color = "var(--zc-text)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--zc-border)"; e.currentTarget.style.color = "var(--zc-text-3)"; }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "20px" }}>

          {/* QUOTE STEP */}
          {step === STEP.QUOTE && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Qty stepper */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px", borderRadius: 10,
                background: "var(--zc-bg-3)", border: "1px solid var(--zc-border)",
              }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--zc-text-2)" }}>Quantity</span>
                <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <button
                    onClick={() => setQty(q => Math.max(1, q - 1))}
                    disabled={qty <= 1}
                    style={{
                      width: 30, height: 30, borderRadius: 7,
                      border: "1.5px solid var(--zc-border-2)", background: "var(--zc-bg-2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: qty <= 1 ? "default" : "pointer", opacity: qty <= 1 ? 0.4 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    <Minus size={12} />
                  </button>
                  <span style={{
                    fontWeight: 700, fontSize: 18,
                    minWidth: 38, textAlign: "center",
                    fontVariantNumeric: "tabular-nums",
                  }}>{qty}</span>
                  <button
                    onClick={() => setQty(q => Math.min(10, q + 1))}
                    disabled={qty >= 10}
                    style={{
                      width: 30, height: 30, borderRadius: 7,
                      border: "1.5px solid var(--zc-border-2)", background: "var(--zc-bg-2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: qty >= 10 ? "default" : "pointer", opacity: qty >= 10 ? 0.4 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>

              {/* Fee breakdown */}
              {quoteLoading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "28px 0", gap: 10, color: "var(--zc-text-3)" }}>
                  <span style={{ width: 18, height: 18, border: "2px solid var(--zc-border)", borderTopColor: "var(--zc-secondary)", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
                  <span style={{ fontSize: 13 }}>Getting quote…</span>
                </div>
              ) : quoteError ? (
                <div style={{ padding: "10px 12px", background: "#FEF2F2", borderRadius: 8, color: "var(--zc-danger)", fontSize: 13, border: "1px solid #FECACA" }}>
                  {quoteError}
                </div>
              ) : fees ? (
                <div style={{ borderRadius: 10, border: "1px solid var(--zc-border)", overflow: "hidden" }}>
                  <FeeRow label="Unit price"       value={formatCents(fees.unitPriceCents)} />
                  <FeeRow label="Quantity"          value={`× ${fees.quantity}`} />
                  <FeeRow label="Subtotal"          value={formatCents(fees.subtotalCents)} />
                  <FeeRow label="Platform fee"      value={formatCents(fees.platformFeeCents)} />
                  <FeeRow label="Transaction fee"   value={formatCents(fees.transactionFeeCents)} />
                  <FeeRow label="Gas fee"           value={formatCents(fees.gasFeeCents)} />
                  <div style={{ borderTop: "2px solid var(--zc-border)", background: "var(--zc-bg-3)" }}>
                    <FeeRow label="Total" value={formatCents(fees.totalCents)} bold accent />
                  </div>
                </div>
              ) : null}

              <button
                onClick={() => setStep(STEP.CONFIRM)}
                disabled={!fees || quoteLoading}
                className="zc-btn-primary"
                style={{ width: "100%", padding: "13px 0", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                <ShoppingCart size={15} />
                Continue to Review
              </button>
            </div>
          )}

          {/* CONFIRM STEP */}
          {step === STEP.CONFIRM && fees && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Purchase summary card */}
              <div style={{
                borderRadius: 12, overflow: "hidden",
                border: "1px solid var(--zc-border)",
              }}>
                <div style={{
                  padding: "20px",
                  background: `linear-gradient(135deg, ${accent}12 0%, ${accent}06 100%)`,
                  borderBottom: "1px solid var(--zc-border)",
                  textAlign: "center",
                }}>
                  {collection.imageUrl ? (
                  <img src={collection.imageUrl} alt={label} style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover", margin: "0 auto 8px", display: "block" }} />
                ) : (
                  <div style={{ fontSize: 36, marginBottom: 8 }}>{icon}</div>
                )}
                  <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--zc-text-3)", marginBottom: 4 }}>You are purchasing</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "var(--zc-text)", marginBottom: 4 }}>{qty} × {label}</div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: accent }}>{formatCents(fees.totalCents)}</div>
                </div>
                <div style={{ padding: "10px 16px", background: "var(--zc-bg-3)", display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--zc-text-3)" }}>
                  <span>Includes all fees</span>
                  <span style={{ fontFamily: "var(--zc-font-mono)" }}>{collection.symbol ?? cat}</span>
                </div>
              </div>

              {error && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "var(--zc-danger)", fontSize: 13 }}>
                  <XCircle size={14} style={{ flexShrink: 0 }} />
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setStep(STEP.QUOTE)}
                  disabled={busy}
                  style={{
                    flex: 1, height: 46, borderRadius: 10,
                    border: "1.5px solid var(--zc-border-2)", background: "transparent",
                    cursor: "pointer", fontSize: 13, fontWeight: 500, color: "var(--zc-text-2)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--zc-bg-3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                >
                  <ArrowLeft size={13} /> Back
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={busy}
                  className="zc-btn-primary"
                  style={{ flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 14, fontWeight: 700 }}
                >
                  {busy
                    ? <><span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Creating order…</>
                    : <>Proceed to Payment</>
                  }
                </button>
              </div>
            </div>
          )}

          {/* PAYMENT STEP */}
          {step === STEP.PAYMENT && clientSecret && (
            stripePromise ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCardForm
                  clientSecret={clientSecret}
                  onSuccess={handleStripeSuccess}
                  onError={setError}
                  totalCents={fees?.totalCents ?? 0}
                  displayName={label}
                  qty={qty}
                  accent={accent}
                />
              </Elements>
            ) : (
              <div style={{ padding: "12px 14px", background: "#FEF2F2", borderRadius: 8, color: "var(--zc-danger)", fontSize: 13, border: "1px solid #FECACA" }}>
                Stripe key not configured. Add <code>STRIPE_PUBLISHABLE_KEY</code> to backend/.env.
              </div>
            )
          )}

          {/* PROCESSING STEP */}
          {step === STEP.PROCESSING && (
            <div style={{ textAlign: "center", padding: "36px 0 28px" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                border: "3px solid var(--zc-secondary-soft)",
                borderTopColor: "var(--zc-secondary)",
                animation: "spin 0.9s linear infinite",
                margin: "0 auto 20px",
              }} />
              <p style={{ fontWeight: 700, fontSize: 16, color: "var(--zc-text)", marginBottom: 6 }}>Transferring tokens…</p>
              <p style={{ color: "var(--zc-text-2)", fontSize: 13, maxWidth: 280, margin: "0 auto" }}>
                Payment confirmed. Your tokens are being transferred to your wallet on-chain.
              </p>
            </div>
          )}

          {/* DONE STEP */}
          {step === STEP.DONE && (
            <div style={{ textAlign: "center", padding: "28px 0 20px" }}>
              {succeeded ? (
                <>
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: "rgba(16,185,129,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 18px",
                  }}>
                    <CheckCircle2 size={32} style={{ color: "var(--zc-success)" }} />
                  </div>
                  <p style={{ fontWeight: 700, fontSize: 17, color: "var(--zc-text)", marginBottom: 6 }}>Tokens Transferred!</p>
                  <p style={{ color: "var(--zc-text-2)", fontSize: 13, marginBottom: 24, maxWidth: 280, margin: "0 auto 24px" }}>
                    {qty} {label}{qty > 1 ? "s" : ""} are now in your investment wallet.
                  </p>
                  <button onClick={onClose} className="zc-btn-primary" style={{ minWidth: 140, display: "inline-flex", alignItems: "center", gap: 7, justifyContent: "center" }}>
                    <CheckCircle2 size={14} /> View Portfolio
                  </button>
                </>
              ) : (
                <>
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: "rgba(239,68,68,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 18px",
                  }}>
                    <XCircle size={32} style={{ color: "var(--zc-danger)" }} />
                  </div>
                  <p style={{ fontWeight: 700, fontSize: 17, color: "var(--zc-text)", marginBottom: 6 }}>Purchase Failed</p>
                  <p style={{ color: "var(--zc-text-2)", fontSize: 13, marginBottom: 24, maxWidth: 280, margin: "0 auto 24px" }}>
                    {order?.errorMessage || "Something went wrong. Please try again."}
                  </p>
                  <button onClick={onClose} style={{
                    minWidth: 120, height: 40, borderRadius: 10,
                    border: "1.5px solid var(--zc-border-2)", background: "transparent",
                    cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--zc-text-2)",
                  }}>
                    Close
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
